<template>
  <v-container
    fluid
    data-cy="change-password-container"
  >
    <v-row no-gutters>
      <v-col class="d-flex justify-start">
        <div class="rounded-circle illustration-container mr-4">
          <sn-illustration
            illustration="Connectivity"
            size="xs"
          />
        </div>
        <div class="d-flex justify-center align-start flex-column">
          <h2
            class="sn-h3"
            data-cy="change-password-title"
          >
            Change password
          </h2>
        </div>
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row pt-8"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="Current password"
          data-cy="current-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="currentPassword"
          class="mb-4 sn-password"
          data-cy="current-password-field"
          sn-input-props_label="Current password"
          :sn-input-props_hint="currentPasswordHint"
          :sn-input-props_error-messages="currentPasswordError"
          @blur="enableCurrentPassValidation"
          @keyup="validateCurrentPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="New password"
          data-cy="new-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="newPassword"
          label="New password"
          class="mb-4 sn-password"
          data-cy="new-password-field"
          :sn-input-props_hint="newPasswordHint"
          :sn-input-props_error-messages="newPasswordError"
          @blur="enableNewPassValidation"
          @keyup="validateNewPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        class="pr-4"
      >
        <sn-label
          title="Confirm new password"
          data-cy="confirm-password-field-label"
          class="pb-2"
        />
        <sn-password
          v-model="confirmPassword"
          label="Confirm new password"
          class="mb-4 sn-password"
          data-cy="confirm-password-field"
          :sn-input-props_error-messages="confirmPasswordHint"
          @blur="enableConfirmPassValidation"
          @keyup="validateConfirmPassword"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-spacer />
      <v-col
        cols="2"
        class="pr-4 pb-4"
      >
        <div class="d-flex align-end flex-column">
          <sn-btn
            style="marginTop: 10px"
            text="Save"
            type="primary"
            size="large"
            data-cy="change-password-save-btn"
            :disabled="!saveEnabled"
            @click="handleSave"
          />
        </div>
      </v-col>
    </v-row>
    <sn-toast
      :value="showSuccessToast"
      :message="successToastMessage"
      :description="successToastDescription"
      event="success"
    />
    <sn-toast
      :value="showErrorToast"
      :message="errorToastMessage"
      event="error"
    />
  </v-container>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import closingPortalService from '~/services/snClosingPortalService'

export default {
  name: 'ChangePassword',

  data () {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      currentPasswordHint: '',
      currentPasswordError: '',
      newPasswordHint: 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number',
      newPasswordError: '',
      confirmPasswordHint: '',
      currentPassValidationEnabled: false,
      newPassValidationEnabled: false,
      confirmPassValidationEnabled: false,
      currentPassIsValid: false,
      newPassIsValid: false,
      confirmPassIsValid: false,
      showSuccessToast: false,
      showErrorToast: false,
      successToastMessage: '',
      successToastDescription: '',
      errorToastMessage: ''
    }
  },

  computed: {
    saveEnabled () {
      return this.currentPassIsValid && this.newPassIsValid && this.confirmPassIsValid
    }
  },

  methods: {
    validateCurrentPassword () {
      if (this.currentPassValidationEnabled) {
        this.currentPassIsValid = !!this.currentPassword
        this.currentPasswordError = this.currentPassIsValid ? '' : this.currentPasswordError
        this.currentPasswordHint = ''
      }
    },
    validateNewPassword () {
      if (this.newPassValidationEnabled) {
        this.newPassIsValid = snValidationsService.password(this.newPassword)
        const newPassMessage = 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number'
        this.newPasswordError = this.newPassIsValid ? '' : newPassMessage
        this.newPasswordHint = this.newPassIsValid ? newPassMessage : ''
      }
      if (this.confirmPassValidationEnabled) {
        this.validateConfirmPassword()
      }
    },
    validateConfirmPassword () {
      if (!this.confirmPassValidationEnabled && this.confirmPassword.length > 8) {
        this.enableConfirmPassValidation()
      } else if (this.confirmPassValidationEnabled) {
        this.confirmPassIsValid = this.newPassword === this.confirmPassword
        this.confirmPasswordHint = this.confirmPassIsValid ? '' : 'Passwords do not match'
      }
    },
    enableCurrentPassValidation () {
      this.currentPassValidationEnabled = true
      this.validateCurrentPassword()
    },
    enableNewPassValidation () {
      this.newPassValidationEnabled = true
      this.validateNewPassword()
    },
    enableConfirmPassValidation () {
      this.confirmPassValidationEnabled = true
      this.validateConfirmPassword()
    },
    handleSave () {
      closingPortalService.updatePassword(this.currentPassword, this.newPassword, this.confirmPassword).then(resp => {
        const message = resp.data.update_password_mutation.message
        if (message === 'Password Updated') {
          Object.assign(this.$data, this.$options.data())
          this.successToastMessage = 'Password updated'
          this.successToastDescription = 'You must login again. You will be redirected momentarily.'
          this.showSuccessToast = true
          setTimeout(() => { window.location.href = '/portal/closing/login' }, 4000)
        } else if (message === 'Incorrect current password') {
          this.currentPassword = ''
          this.currentPasswordError = message
          this.currentPassIsValid = false
          this.errorToastMessage = message
          this.showErrorToast = true
          setTimeout(() => { this.showErrorToast = false }, 6000)
        } else if (message === 'Invalid User') {
          window.location.href = '/portal/closing/login'
        } else {
          this.errorToastMessage = message
          this.showErrorToast = true
          setTimeout(() => { this.showErrorToast = false }, 6000)
        }
      })
        .catch(e => {
          this.errorToastMessage = 'An error occurred while updating this information. Please try again later.'
          this.showErrorToast = true
          setTimeout(() => { this.showErrorToast = false }, 6000)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.illustration-container {
  background-color: $sn-brand-01;
  width: 112px;
  height: 112px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-row {
  width: 100%;
}

.sn-password {
  min-width: 50%;
  width: 528px;
  max-width: 100%;
}
</style>
